import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus } from 'lucide-react';

export const EmployeeProfileDialog = ({
  open,
  onOpenChange,
  employeeProfile,
  onAddCertificate,
  onAddLicense,
  onAddCredential,
  onDeleteCertificate,
  onDeleteLicense,
  onDeleteCredential
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="employee-profile-dialog">
      <DialogHeader>
        <DialogTitle>
          Employee Profile: {employeeProfile?.user?.name || employeeProfile?.name || 'Loading...'}
        </DialogTitle>
      </DialogHeader>
      {employeeProfile && (
        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-gray-500">Email</Label>
              <p className="font-medium">{employeeProfile.user?.email || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-gray-500">Phone</Label>
              <p className="font-medium">{employeeProfile.user?.phone || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-gray-500">Role</Label>
              <p className="font-medium capitalize">{employeeProfile.role || employeeProfile.user?.role || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-gray-500">Hourly Rate</Label>
              <p className="font-medium">${employeeProfile.hourly_rate || 0}/hr</p>
            </div>
            <div>
              <Label className="text-gray-500">YTD Hours</Label>
              <p className="font-medium text-blue-600">{employeeProfile.ytd_hours || 0} hours</p>
            </div>
            <div>
              <Label className="text-gray-500">Hire Date</Label>
              <p className="font-medium">{employeeProfile.hire_date || 'N/A'}</p>
            </div>
          </div>

          {/* Certificates Section */}
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-lg">📜 Certificates</h3>
              <Button size="sm" onClick={onAddCertificate}>
                <Plus className="w-4 h-4 mr-1" /> Add Certificate
              </Button>
            </div>
            {employeeProfile.certificates?.length > 0 ? (
              <div className="space-y-2">
                {employeeProfile.certificates.map((cert) => {
                  const isExpired = cert.expiry_date && new Date(cert.expiry_date) < new Date();
                  return (
                    <div 
                      key={cert.id} 
                      className={`flex justify-between items-center p-3 rounded ${isExpired ? 'bg-red-50 border border-red-200' : 'bg-green-50'}`}
                    >
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {cert.name}
                          {isExpired && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">EXPIRED</span>}
                        </p>
                        <p className="text-sm text-gray-600">
                          {cert.issuing_authority} • #{cert.certificate_number}
                        </p>
                        <p className={`text-xs ${isExpired ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                          Expires: {cert.expiry_date || 'No expiry'}
                        </p>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => onDeleteCertificate(employeeProfile.id, cert.id)}>
                        Delete
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No certificates added</p>
            )}
          </div>

          {/* Licenses Section */}
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-lg">🪪 Licenses</h3>
              <Button size="sm" onClick={onAddLicense}>
                <Plus className="w-4 h-4 mr-1" /> Add License
              </Button>
            </div>
            {employeeProfile.licenses?.length > 0 ? (
              <div className="space-y-2">
                {employeeProfile.licenses.map((lic) => {
                  const isExpired = lic.expiry_date && new Date(lic.expiry_date) < new Date();
                  return (
                    <div 
                      key={lic.id} 
                      className={`flex justify-between items-center p-3 rounded ${isExpired ? 'bg-red-50 border border-red-200' : 'bg-purple-50'}`}
                    >
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {lic.license_type}
                          {isExpired && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">EXPIRED</span>}
                        </p>
                        <p className="text-sm text-gray-600">
                          #{lic.license_number} • {lic.state || 'N/A'}
                        </p>
                        <p className={`text-xs ${isExpired ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                          Expires: {lic.expiry_date || 'No expiry'}
                        </p>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => onDeleteLicense(employeeProfile.id, lic.id)}>
                        Delete
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No licenses added</p>
            )}
          </div>

          {/* Credentials Section */}
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-lg">🏅 Credentials</h3>
              <Button size="sm" onClick={onAddCredential}>
                <Plus className="w-4 h-4 mr-1" /> Add Credential
              </Button>
            </div>
            {employeeProfile.credentials?.length > 0 ? (
              <div className="space-y-2">
                {employeeProfile.credentials.map((cred) => {
                  const isExpired = cred.expiry_date && new Date(cred.expiry_date) < new Date();
                  return (
                    <div 
                      key={cred.id} 
                      className={`flex justify-between items-center p-3 rounded ${isExpired ? 'bg-red-50 border border-red-200' : 'bg-blue-50'}`}
                    >
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {cred.credential_type}
                          {isExpired && <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">EXPIRED</span>}
                        </p>
                        <p className="text-sm text-gray-600">
                          #{cred.credential_number || 'N/A'}
                        </p>
                        <p className={`text-xs ${isExpired ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                          Expires: {cred.expiry_date || 'No expiry'} {cred.notes && `• ${cred.notes}`}
                        </p>
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => onDeleteCredential(employeeProfile.id, cred.id)}>
                        Delete
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No credentials added</p>
            )}
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const AddCertificateDialog = ({
  open,
  onOpenChange,
  newCertificate,
  setNewCertificate,
  onSave,
  onFileUpload,
  isUploading
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="add-certificate-dialog">
      <DialogHeader>
        <DialogTitle>Add Certificate</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label htmlFor="cert_name">Certificate Name *</Label>
          <Input
            id="cert_name"
            value={newCertificate.name}
            onChange={(e) => setNewCertificate({ ...newCertificate, name: e.target.value })}
            placeholder="e.g., WHMIS Training"
          />
        </div>
        <div>
          <Label htmlFor="cert_issuer">Issuing Authority</Label>
          <Input
            id="cert_issuer"
            value={newCertificate.issuing_authority}
            onChange={(e) => setNewCertificate({ ...newCertificate, issuing_authority: e.target.value })}
            placeholder="e.g., Ontario Safety Council"
          />
        </div>
        <div>
          <Label htmlFor="cert_number">Certificate Number</Label>
          <Input
            id="cert_number"
            value={newCertificate.certificate_number}
            onChange={(e) => setNewCertificate({ ...newCertificate, certificate_number: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cert_issue">Issue Date</Label>
            <Input
              id="cert_issue"
              type="date"
              value={newCertificate.issue_date}
              onChange={(e) => setNewCertificate({ ...newCertificate, issue_date: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="cert_expiry">Expiry Date</Label>
            <Input
              id="cert_expiry"
              type="date"
              value={newCertificate.expiry_date}
              onChange={(e) => setNewCertificate({ ...newCertificate, expiry_date: e.target.value })}
            />
          </div>
        </div>
        <div className="border-2 border-dashed rounded-lg p-4">
          <Label htmlFor="cert_file" className="flex items-center gap-2 text-red-600 font-medium">
            📎 Document Upload * (Required)
          </Label>
          <p className="text-xs text-gray-500 mb-2">Upload a scan or photo of the certificate</p>
          <Input
            id="cert_file"
            type="file"
            accept="image/*,.pdf"
            onChange={onFileUpload}
            disabled={isUploading}
          />
          {newCertificate.document_url && (
            <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
              ✅ Document uploaded successfully
            </div>
          )}
          {isUploading && (
            <div className="mt-2 text-sm text-blue-600">
              ⏳ Uploading document...
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSave} 
            disabled={!newCertificate.name || !newCertificate.document_url || isUploading}
          >
            Save Certificate
          </Button>
        </div>
        {!newCertificate.document_url && newCertificate.name && (
          <p className="text-xs text-amber-600 text-center">
            ⚠️ Please upload a document before saving
          </p>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export const AddLicenseDialog = ({
  open,
  onOpenChange,
  newLicense,
  setNewLicense,
  onSave,
  onFileUpload,
  isUploading
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="add-license-dialog">
      <DialogHeader>
        <DialogTitle>Add License</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label htmlFor="lic_type">License Type *</Label>
          <Input
            id="lic_type"
            value={newLicense.license_type}
            onChange={(e) => setNewLicense({ ...newLicense, license_type: e.target.value })}
            placeholder="e.g., Driver's License Class G"
          />
        </div>
        <div>
          <Label htmlFor="lic_number">License Number *</Label>
          <Input
            id="lic_number"
            value={newLicense.license_number}
            onChange={(e) => setNewLicense({ ...newLicense, license_number: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="lic_state">State/Province</Label>
          <Input
            id="lic_state"
            value={newLicense.state}
            onChange={(e) => setNewLicense({ ...newLicense, state: e.target.value })}
            placeholder="e.g., Ontario"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="lic_issue">Issue Date</Label>
            <Input
              id="lic_issue"
              type="date"
              value={newLicense.issue_date}
              onChange={(e) => setNewLicense({ ...newLicense, issue_date: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="lic_expiry">Expiry Date</Label>
            <Input
              id="lic_expiry"
              type="date"
              value={newLicense.expiry_date}
              onChange={(e) => setNewLicense({ ...newLicense, expiry_date: e.target.value })}
            />
          </div>
        </div>
        <div className="border-2 border-dashed rounded-lg p-4">
          <Label htmlFor="lic_file" className="flex items-center gap-2 text-red-600 font-medium">
            📎 Document Upload * (Required)
          </Label>
          <p className="text-xs text-gray-500 mb-2">Upload a scan or photo of the license</p>
          <Input
            id="lic_file"
            type="file"
            accept="image/*,.pdf"
            onChange={onFileUpload}
            disabled={isUploading}
          />
          {newLicense.document_url && (
            <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
              ✅ Document uploaded successfully
            </div>
          )}
          {isUploading && (
            <div className="mt-2 text-sm text-blue-600">
              ⏳ Uploading document...
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={onSave} 
            disabled={!newLicense.license_type || !newLicense.license_number || !newLicense.document_url || isUploading}
          >
            Save License
          </Button>
        </div>
        {!newLicense.document_url && newLicense.license_type && (
          <p className="text-xs text-amber-600 text-center">
            ⚠️ Please upload a document before saving
          </p>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export const AddCredentialDialog = ({
  open,
  onOpenChange,
  newCredential,
  setNewCredential,
  onSave
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="add-credential-dialog">
      <DialogHeader>
        <DialogTitle>Add Credential</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div>
          <Label htmlFor="cred_type">Credential Type *</Label>
          <Input
            id="cred_type"
            value={newCredential.credential_type}
            onChange={(e) => setNewCredential({ ...newCredential, credential_type: e.target.value })}
            placeholder="e.g., OSHA 30-Hour"
          />
        </div>
        <div>
          <Label htmlFor="cred_number">Credential Number</Label>
          <Input
            id="cred_number"
            value={newCredential.credential_number}
            onChange={(e) => setNewCredential({ ...newCredential, credential_number: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cred_issue">Issue Date</Label>
            <Input
              id="cred_issue"
              type="date"
              value={newCredential.issue_date}
              onChange={(e) => setNewCredential({ ...newCredential, issue_date: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="cred_expiry">Expiry Date</Label>
            <Input
              id="cred_expiry"
              type="date"
              value={newCredential.expiry_date}
              onChange={(e) => setNewCredential({ ...newCredential, expiry_date: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="cred_notes">Notes</Label>
          <Textarea
            id="cred_notes"
            value={newCredential.notes}
            onChange={(e) => setNewCredential({ ...newCredential, notes: e.target.value })}
            rows={2}
          />
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={!newCredential.credential_type}>Save Credential</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default {
  EmployeeProfileDialog,
  AddCertificateDialog,
  AddLicenseDialog,
  AddCredentialDialog
};
